import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, WritableSignal, signal } from '@angular/core';
import { catchError, finalize, Observable, of, tap } from 'rxjs';
import { ToastService } from './toast.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root'
})


export class DataService {
  isLoading: WritableSignal<boolean> = signal(false);
  private activeRequests: number = 0;
  
  constructor(private http: HttpClient, private toast: ToastService,

  ) { }

  private finalizeLoading(): () => void {
    return () => {
      this.activeRequests--;
      if (this.activeRequests <= 0) {
        this.activeRequests = 0;
        this.isLoading.set(false);
      }
    };
  }

  private startLoading(): void {
    this.activeRequests++;
    this.isLoading.set(true);
  }

  get(path: string, message?: string, isLoading: boolean = true): Observable<any> {
    if (isLoading) {
      this.startLoading();
    }

    // Add cache-busting param
    const fullPath = path.includes('?')
      ? `${path}&_=${Date.now()}`
      : `${path}?_=${Date.now()}`;

    return this.http.get(fullPath, httpOptions)
      .pipe(
        tap(() => this.handleSuccess(message)),
        catchError((err: any) => this.handleError(err)),
        finalize(isLoading ? this.finalizeLoading() : () => {})
      );
  }


  post(path: string, payload: any, message?: string): Observable<any> {
    this.startLoading();
    return this.http.post(path, payload)
      .pipe(
        tap(() => this.handleSuccess(message)),
        catchError((err: any) => this.handleError(err)),
        finalize(this.finalizeLoading())
      );
  }

  postWithoutLoading<T>(path: string, payload: any, message?: string): Observable<T> {
    return this.http.post<T>(path, payload)
      .pipe(
        tap(() => this.handleSuccess(message)),
        catchError((err: any) => this.handleError(err))
      );
  }

  postWithOptions(path: string, payload: any, options: any, message?: string): Observable<any> {
    this.startLoading();
    return this.http.post(path, payload, options)
      .pipe(
        tap(() => this.handleSuccess(message)),
        catchError((err: any) => this.handleError(err)),
        finalize(this.finalizeLoading())
      );
  }

  postTyped<T>(path: string, payload: any, message?: string): Observable<T> {
    this.startLoading();
  return this.http.post<T>(path, payload)
    .pipe(
      tap(() => this.handleSuccess(message)),
      catchError((err: any) => this.handleError(err)),
      finalize(this.finalizeLoading())
    );
  }

  // postWithOptionsTyped<T>(path: string, payload: any, options: any, message?: string): Observable<T> {
  //   this.isLoading.set(true);
  //   return this.http.post<T>(path, payload, options)
  //     .pipe(
  //       tap(() => this.handleSuccess(message)),
  //       catchError((err: any) => this.handleError(err)),
  //       finalize(() => this.isLoading.set(false))
  //     );
  // }



  delete(path: string, message?: string): Observable<any> {
    this.startLoading();
    return this.http.delete(path, httpOptions)
      .pipe(
        tap(() => this.handleSuccess(message)),
        catchError((err: any) => this.handleError(err)),
        finalize(this.finalizeLoading())
      );
  }

  patch(path: string, payload: any, message?: string): Observable<any> {
    this.startLoading();
    return this.http.patch(path, payload)
      .pipe(
        tap(() => this.handleSuccess(message)),
        catchError((err: any) => this.handleError(err)),
        finalize(this.finalizeLoading())
      );
  }

  put(path: string, payload: unknown, message?: string): Observable<any> {
    this.startLoading();
    return this.http.put(path, payload)
      .pipe(
        tap(() => this.handleSuccess(message)),
        catchError((err: any) => this.handleError(err)),
        finalize(this.finalizeLoading())
      );
  }

    getBlob(path: string, message?: string, isLoading: boolean = true): Observable<Blob> {
    if (isLoading) {
      this.startLoading();
    }
    const fullPath = path.includes('?') ? `${path}&_=${Date.now()}` : `${path}?_=${Date.now()}`;

    return this.http.get(fullPath, { responseType: 'blob' }).pipe(
      tap(() => this.handleSuccess(message)),
      catchError((err: any) => this.handleError(err)),
      finalize(isLoading ? this.finalizeLoading() : () => {})
    ) as Observable<Blob>;
  }

  private handleSuccess(message?: string) {
    if (message) {
      this.toast.success(message)
    }
  }

  private handleError(err: HttpErrorResponse): never {
    const message = this.extractErrorMessage(err);
    this.toast.error(message);

    throw err;
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    if (!err.error) return 'An unknown error occurred.';

    if (typeof err.error === 'string') {
      return err.error;
    }

    return err.error.message || err.error.ErrorMessage || err.message || 'An error occurred.';
  }
}
